import React from 'react';
import _ from 'lodash';

import rxUi from 'ub/control/rx-ui';
import SOURCES from 'ui/asset-library/asset-sources';
import { TYPES } from 'ui/asset-library/asset-types';
import { assetTypePropType } from './prop-types';

import AssetLibrary from './asset-library';

function generateTotals(items = []) {
  return Object.keys(TYPES).reduce(
    (acc, type) => ({
      ...acc,
      [type]: items.filter(item => item.type === type).length,
    }),
    {}
  );
}

function compareItemsForSort(a, b, sortBy, sortAscending) {
  const first = sortAscending ? a : b;
  const second = sortAscending ? b : a;

  switch (sortBy) {
    case 'filename':
      return first.asset.content_file_name === second.asset.content_file_name
        ? compareItemsForSort(a, b, 'date', sortAscending)
        : first.asset.content_file_name.localeCompare(second.asset.content_file_name);

    case 'filesize':
      return first.asset.content_file_size === second.asset.content_file_size
        ? compareItemsForSort(a, b, 'date', sortAscending)
        : first.asset.content_file_size > second.asset.content_file_size
          ? 1
          : -1;

    case 'date':
    default:
      return first.asset.id > second.asset.id ? 1 : -1;
  }
}

export default class AssetLibraryContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      authToken: null,
      failedUploads: [],
      isConfirmingDelete: false,
      isOpen: false,
      searchTerm: '',
      selectedItemIds: [],
      selectedSource: 'currentClient',
      selectedType: 'images',
      sortAscending: false,
      sortBy: 'date',
      sources: {},
      uploadsInProgress: 0,
    };

    this.uiBinding = new rxUi.Binding(['AssetLibrary']);
    this.uiBinding.subscribe(this.handleUIEvent.bind(this));

    this.dispatch = this.dispatch.bind(this);
    this.handleSelectFilesForUpload = this.handleSelectFilesForUpload.bind(this);
    this.handleScrollNearBottom = this.handleScrollNearBottom.bind(this);

    this.handleChangeSearch = _.debounce(this.handleChangeSearch.bind(this), 500);
  }

  handleChangeSearch() {
    const { searchTerm, selectedSource } = this.state;

    if (!SOURCES[selectedSource].fetchByKeyword) {
      return;
    }

    if (searchTerm) {
      this.setState({
        sources: { ...this.state.sources, [this.state.selectedSource]: undefined },
      });

      SOURCES[selectedSource]
        .fetchByKeyword(searchTerm, 1)
        .then(({ fetchedItems, totalPages }) =>
          this.setState({
            sources: {
              ...this.state.sources,
              [selectedSource]: { page: 1, totalPages, items: fetchedItems },
            },
          })
        )
        .catch(fetchError =>
          this.setState({
            sources: {
              ...this.state.sources,
              [selectedSource]: { page: 0, totalPages: 0, items: [], fetchError },
            },
          })
        );
    } else {
      this.setState({
        sources: {
          ...this.state.sources,
          [selectedSource]: { page: 0, totalPages: 0, items: [] },
        },
      });
    }
  }

  componentDidMount() {
    const authTokenMetaTag = document.querySelector('meta[name=csrf-token]');

    if (authTokenMetaTag) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ authToken: authTokenMetaTag.content });
    }
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      isOpen: nextProps.isOpen,
      selectedItemIds: nextProps.selectedAssetUuids || [],
      selectedType: nextProps.selectedType || this.state.selectedType,

      // AssetLibraryWrapper only re-renders AssetLibraryContainer with new props when triggering an
      // open, so we can also use componentWillReceiveProps to reset some state each time the asset
      // library is opened.
      failedUploads: [],
      isConfirmingDelete: false,
      searchTerm: '',
      selectedSource: 'currentClient',
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (this.state.isOpen && !prevState.isOpen) ||
      this.state.selectedSource !== prevState.selectedSource
    ) {
      this.fetchItems();
    }
  }

  fetchItems() {
    const { selectedSource, sources } = this.state;

    if (sources[selectedSource]) {
      return;
    }

    if (SOURCES[selectedSource].fetch) {
      SOURCES[selectedSource]
        .fetch()
        .then(({ totalPages, fetchedItems }) =>
          this.setState({
            sources: {
              ...this.state.sources,
              [selectedSource]: { page: 1, totalPages, items: fetchedItems },
            },
          })
        )
        .catch(fetchError =>
          this.setState({
            sources: {
              ...this.state.sources,
              [selectedSource]: { page: 0, totalPages: 0, items: [], fetchError },
            },
          })
        );
    } else {
      this.setState({
        sources: {
          ...this.state.sources,
          [selectedSource]: { page: 0, totalPages: 0, items: [] },
        },
      });
    }
  }

  dispatch(event) {
    this.uiBinding.onNext(event);
  }

  handleUIEvent(event) {
    switch (event.type) {
      case 'changeType':
        return this.setState({ selectedType: event.assetType });

      case 'changeSort': {
        // If the user clicked the currently-active sort value, toggle the sort order. Otherwise,
        // set order to the default for the new sort value.  'Created' should default to descending,
        // all others should default to ascending.
        const defaultSortAscending = event.sortBy === 'date' ? false : true;
        return this.setState({
          sortAscending:
            event.sortBy === this.state.sortBy ? !this.state.sortAscending : defaultSortAscending,
          sortBy: event.sortBy,
        });
      }

      case 'changeSource':
        return this.setState({
          searchTerm: '',
          selectedSource: event.source,
        });

      case 'selectItem':
        return this.setState({ selectedItemIds: this.calculateNewSelectedItemIds(event) });

      case 'choose': {
        const chosenItems = this.state.sources[this.state.selectedSource].items.filter(item =>
          this.state.selectedItemIds.includes(item.id)
        );

        if (SOURCES[this.state.selectedSource].isExternal) {
          this.handleImportItems(chosenItems);
        } else {
          this.props.onChoose(chosenItems[0].asset);
          this.setState({ isOpen: false });
        }
        return;
      }

      case 'changeSearch': {
        return this.setState({ searchTerm: event.value }, () => this.handleChangeSearch());
      }

      case 'startDelete':
        return this.setState({ isConfirmingDelete: true });

      case 'cancelDelete':
        return this.setState({ isConfirmingDelete: false });

      case 'confirmDelete': {
        this.setState({ isConfirmingDelete: false });

        return this.deleteSelectedItems();
      }

      case 'close': {
        this.props.onClose();
        return this.setState({ isOpen: false });
      }
    }
  }

  calculateNewSelectedItemIds(event) {
    const previousSelectedItemIds = this.state.selectedItemIds;

    if (event.shiftKey && previousSelectedItemIds.length) {
      // Replace with range selection
      const items = this.getVisibleItems();

      const clickedIndex = items.findIndex(item => item.id === event.itemId);
      const selectedIndex = Math.min(
        ...previousSelectedItemIds.map(itemId => items.findIndex(item => item.id === itemId))
      );

      const selectionIndices =
        clickedIndex > selectedIndex
          ? _.range(selectedIndex, clickedIndex + 1)
          : _.range(clickedIndex, selectedIndex + 1);

      return selectionIndices.map(index => items[index].id);
    } else if (event.metaKey || event.ctrlKey) {
      // Append to or remove from selection
      return previousSelectedItemIds.includes(event.itemId)
        ? previousSelectedItemIds.filter(itemId => itemId !== event.itemId)
        : [...previousSelectedItemIds, event.itemId];
    } else {
      // Replace selection
      return [event.itemId];
    }
  }

  handleSelectFilesForUpload(files) {
    const { authToken, selectedSource } = this.state;
    const { visibleTypes } = this.props;

    const onlyImages =
      !SOURCES[selectedSource].isExternal && // onlyImages=false allows a higher file size limit
      visibleTypes.length === 1 &&
      visibleTypes[0] === 'images';

    this.setState({ uploadsInProgress: files.length, failedUploads: [] });

    SOURCES.currentClient.upload(files, authToken, onlyImages).subscribe(
      result => {
        // The Observable will emit a value for each successful or failed upload
        if (result.error) {
          this.setState({
            failedUploads: [
              ...this.state.failedUploads,
              `${result.file.name} (${result.error.message})`,
            ],
            uploadsInProgress: this.state.uploadsInProgress - 1,
          });
        } else {
          this.setState({
            sources: {
              ...this.state.sources,
              currentClient: {
                ...this.state.sources.currentClient,
                items: [...this.state.sources.currentClient.items, result.item],
              },
            },
            selectedItemIds: [result.item.id],
            selectedType: result.item.type,
            uploadsInProgress: this.state.uploadsInProgress - 1,
          });
        }
      },
      () => this.setState({ uploadsInProgress: 0 }), // onError
      () =>
        this.setState({
          searchTerm: '',
          selectedSource: 'currentClient',
          sources: SOURCES[selectedSource].isExternal
            ? { ...this.state.sources, [selectedSource]: undefined }
            : this.state.sources,
          uploadsInProgress: 0,
        }) // onComplete
    );
  }

  handleImportItems(items) {
    const { selectedSource } = this.state;

    this.setState({ uploadsInProgress: items.length });

    Promise.all(
      // Download images from source
      items.map(item =>
        fetch(item.fullUrl)
          .then(res => res.blob())
          .then(blob => new File([blob], item.filename, { type: 'image/jpeg' }))
      )
    ).then(files =>
      // Upload images to webapp
      this.handleSelectFilesForUpload(files)
    );

    if (SOURCES[selectedSource].onChoose) {
      SOURCES[selectedSource].onChoose(items);
    }
  }

  handleScrollNearBottom() {
    const { searchTerm, selectedSource, sources } = this.state;

    if (!sources[selectedSource]) {
      return;
    }

    const { page, totalPages, isFetchingMore } = sources[selectedSource];

    if (page < totalPages && !isFetchingMore) {
      this.setState({
        sources: {
          ...sources,
          [selectedSource]: {
            ...sources[selectedSource],
            isFetchingMore: true,
          },
        },
      });

      const fetch = SOURCES[selectedSource].fetchByKeyword
        ? SOURCES[selectedSource].fetchByKeyword(searchTerm, page + 1)
        : SOURCES[selectedSource].fetch(page + 1);

      fetch
        .then(({ fetchedItems, totalPages }) =>
          this.setState({
            sources: {
              ...this.state.sources,
              [selectedSource]: {
                isFetchingMore: false,
                items: [...sources[selectedSource].items, ...fetchedItems],
                page: page + 1,
                totalPages,
              },
            },
          })
        )
        .catch(() => {});
    }
  }

  deleteSelectedItems() {
    const { authToken, sources, selectedItemIds, selectedSource } = this.state;

    this.setState({
      sources: {
        ...sources,
        currentClient: {
          ...sources.currentClient,
          items: sources.currentClient.items.filter(item => !selectedItemIds.includes(item.id)),
        },
      },
    });

    // The asset delete endpoint accepts asset integer IDs, not UUIDs
    const assetIntegerIds = selectedItemIds
      .map(itemId => this.state.sources[selectedSource].items.find(item => item.id === itemId))
      .filter(Boolean)
      .map(asset => asset.asset.id);

    SOURCES.currentClient.deleteItems(assetIntegerIds, authToken).catch(() => {});
  }

  getVisibleItems() {
    const { sources, searchTerm, selectedSource, selectedType, sortAscending, sortBy } = this.state;
    const { items } = sources[selectedSource] || {};

    if (!items) {
      return [];
    }

    const sortedItems = SOURCES[selectedSource].isExternal
      ? items
      : [...items] // Clone to prevent Array::sort from mutating state
          .filter(item => item.type === selectedType)
          .sort((a, b) => compareItemsForSort(a, b, sortBy, sortAscending));

    if (SOURCES[selectedSource].filterByKeyword && searchTerm) {
      return SOURCES[selectedSource].filterByKeyword(sortedItems, searchTerm);
    } else {
      return sortedItems;
    }
  }

  render() {
    const {
      endpoint,
      failedUploads,
      isConfirmingDelete,
      isOpen,
      searchTerm,
      selectedItemIds,
      selectedSource,
      selectedType,
      sortAscending,
      sortBy,
      sources,
      uploadsInProgress,
    } = this.state;

    const { uploadButtonText, title, visibleTypes } = this.props;

    const sourceProps = SOURCES[selectedSource];

    return isOpen ? (
      <AssetLibrary
        allowChoose={
          sourceProps.isExternal
            ? selectedItemIds.length > 0 && selectedItemIds.length <= 5
            : selectedItemIds.length === 1
        }
        allowDelete={Boolean(
          sourceProps.deleteItems && selectedItemIds.length > 0 && sources[selectedSource]
        )}
        allowSort={!sourceProps.isExternal}
        allowUpload={typeof sourceProps.upload === 'function'}
        chooseButtonText={sourceProps.isExternal ? 'Add to Library' : 'Choose'}
        dispatch={this.dispatch}
        emptyMessage={
          typeof SOURCES[selectedSource].fetchByKeyword === 'function'
            ? 'Enter a search term above to start looking for images.'
            : "Uh-oh! You don't have any files available."
        }
        failedUploads={failedUploads}
        fetchError={sources[selectedSource] ? sources[selectedSource].fetchError : null}
        isConfirmingDelete={isConfirmingDelete}
        isLoading={!sources[selectedSource]}
        items={this.getVisibleItems()}
        largeThumbnails={SOURCES[selectedSource].isExternal === true}
        onScrollNearBottom={this.handleScrollNearBottom}
        onSelectFilesForUpload={this.handleSelectFilesForUpload}
        searchTerm={searchTerm}
        selectedItemIds={selectedItemIds}
        selectedSource={selectedSource}
        selectedType={selectedType}
        sortAscending={sortAscending}
        sortBy={sortBy}
        title={title}
        typeTotals={generateTotals(sources[selectedSource] ? sources[selectedSource].items : [])}
        uploadButtonText={uploadButtonText}
        uploadEndpoint={endpoint}
        uploadingText={
          SOURCES[selectedSource].isExternal ? 'Importing files...' : 'Uploading files...'
        }
        uploadsInProgress={uploadsInProgress}
        visibleTypes={visibleTypes}
      />
    ) : null;
  }
}

AssetLibraryContainer.defaultProps = {
  onChoose: () => {},
  onClose: () => {},
  selectedAssetUuids: [],
  selectedType: 'images',
  title: 'Choose an Image',
  visibleTypes: Object.keys(TYPES),
};

AssetLibraryContainer.propTypes = {
  isOpen: React.PropTypes.bool.isRequired,
  onChoose: React.PropTypes.func,
  onClose: React.PropTypes.func,
  selectedAssetUuids: React.PropTypes.arrayOf(React.PropTypes.string),
  selectedType: assetTypePropType,
  title: React.PropTypes.string,
  uploadButtonText: React.PropTypes.string,
  visibleTypes: React.PropTypes.arrayOf(assetTypePropType),
};



// WEBPACK FOOTER //
// ./src/components/asset-library/asset-library-container.jsx